import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Badge from '../../components/shared/Badge/Badge';
import StyledCheckbox from '../../components/shared/StyledCheckbox/StyledCheckbox';
import { EvaluationCommentRule, EvaluationQuestionType } from '../../util/evaluationContract';
import { TAG_CHIP_STYLE } from '../../util/evaluationStatusMeta';
import BtButton from '../bt-button/bt-button';
import { PreviewQuestion } from './wizardState';
import styles from './app-evaluation-wizard.module.scss';

export type TemplateQuestionsProps = {
  questions: PreviewQuestion[];
  onToggleRemoved: (key: string) => void;
  onSetRequired: (key: string, required: boolean) => void;
  onSetAllowSkip: (key: string, allowSkip: boolean) => void;
};

const COMMENT_RULE: Record<string, [string, string]> = {
  [EvaluationCommentRule.None]: ['wizard.template.comment.none', 'Kommentar valfri'],
  [EvaluationCommentRule.Below3]: [
    'wizard.template.comment.below3',
    'Kommentar krävs vid betyg under 3',
  ],
  [EvaluationCommentRule.Always]: ['wizard.template.comment.always', 'Kommentar krävs alltid'],
};

type RuleBoxProps = { label: string; checked: boolean; onChange: (checked: boolean) => void };

const RuleBox = ({ label, checked, onChange }: RuleBoxProps) => (
  <label className={styles.ruleBox}>
    <StyledCheckbox
      checked={checked}
      onChange={(_event: ChangeEvent<HTMLInputElement>, value: boolean) => onChange(value)}
      inputProps={{ 'aria-label': label }}
    />
    <span>{label}</span>
  </label>
);

const TemplateQuestions = ({
  questions,
  onToggleRemoved,
  onSetRequired,
  onSetAllowSkip,
}: TemplateQuestionsProps) => {
  const [t] = useTranslation('evaluations');
  let asked = 0;

  const removeButton = (question: PreviewQuestion) => (
    <BtButton color="white" size="xxs" tabIndex={0} onClick={() => onToggleRemoved(question.key)}>
      {question.removed
        ? t('wizard.template.undoRemove', 'Ångra')
        : t('wizard.template.remove', 'Ta bort')}
    </BtButton>
  );

  return (
    <ul className={styles.templateQuestions} data-template-questions>
      {questions.map(question => {
        if (question.removed) {
          return (
            <li key={question.key} className={styles.templateQuestion}>
              <span className={styles.numberless} />
              <span className={styles.questionBody}>
                <span className={styles.removedText}>{question.text}</span>
                <span className={`${styles.small} ${styles.muted}`}>
                  {t('wizard.template.removedNote', 'Tas inte med i den här utvärderingen')}
                </span>
              </span>
              <span className={styles.questionActions} data-question-actions>
                {removeButton(question)}
              </span>
            </li>
          );
        }
        if (question.type === EvaluationQuestionType.Paragraph) {
          return (
            <li key={question.key} className={styles.templateQuestion}>
              <span className={styles.instruction}>
                <span>{question.text}</span>
                <span className={styles.optional}>
                  {t('wizard.template.paragraph', 'Visas som text, räknas inte')}
                </span>
              </span>
              <span className={styles.questionActions} data-question-actions>
                {removeButton(question)}
              </span>
            </li>
          );
        }
        asked += 1;
        const isRating = question.type === EvaluationQuestionType.Rating;
        const [commentKey, commentFallback] =
          COMMENT_RULE[question.commentRule] ?? COMMENT_RULE.none;
        return (
          <li key={question.key} className={styles.templateQuestion}>
            <span className={styles.questionNumber}>{asked}</span>
            <span className={styles.questionBody}>
              <span>{question.text}</span>
              {question.added && (
                <span className={styles.badgeRow}>
                  <Badge
                    color={TAG_CHIP_STYLE.color}
                    backgroundColor={TAG_CHIP_STYLE.backgroundColor}
                    className={styles.badge}
                  >
                    {t('wizard.template.added', 'Tillagd')}
                  </Badge>
                </span>
              )}
              {isRating && (
                <span className={`${styles.small} ${styles.muted}`}>
                  {t(commentKey, commentFallback)}
                </span>
              )}
            </span>
            <span className={styles.questionActions} data-question-actions>
              <RuleBox
                label={t('wizard.template.required', 'Obligatorisk')}
                checked={question.required}
                onChange={required => onSetRequired(question.key, required)}
              />
              {isRating && (
                <RuleBox
                  label={t('wizard.template.allowSkip', 'Tillåt Vet ej')}
                  checked={question.allowSkip}
                  onChange={allowSkip => onSetAllowSkip(question.key, allowSkip)}
                />
              )}
              {removeButton(question)}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

export default TemplateQuestions;
